import Header from '@/components/partners/Header'
import Footer from '@/components/partners/Footer'
import { motion } from 'framer-motion'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button-partners'
import { useNavigate } from 'react-router-dom'

const SobreNos = () => {
  const navigate = useNavigate()
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <section className="w-full py-20 md:py-28 bg-background">
          <div className="max-w-6xl mx-auto px-6 md:px-12">
            {/* Botão Voltar */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-8"
            >
              <Button
                variant="ghost"
                onClick={() => navigate('/parcerias')}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft size={18} />
                Voltar à página inicial
              </Button>
            </motion.div>

            {/* Layout: Texto à esquerda, Vídeo à direita */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Texto à esquerda */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="text-xs md:text-sm font-sans uppercase tracking-widest text-primary font-medium">
                  Sobre Nós
                </span>
                <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold italic text-foreground mt-4 mb-6">
                  Nosso Propósito
                </h1>

                <div
                  className="space-y-6 text-muted-foreground leading-relaxed text-sm md:text-base"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  <div>
                    <p>
                      Acreditamos que as melhores memórias são aquelas que
                      podemos tocar e reconhecemos o poder de uma história bem
                      contada. Cada história é única. Nossa missão é entregar
                      mais do que um álbum comum; é construir uma narrativa
                      visual com um design profissional exclusivo que valorize a
                      sua trajetória.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-sans text-base md:text-lg font-bold text-foreground mb-2 tracking-wide">
                      Experiência e Solidez
                    </h2>
                    <p>
                      Contamos com a segurança de quem conhece profundamente o
                      setor: nossos fundadores atuam há mais de 20 anos no
                      mercado de fotografia e pós-produção. Essa bagagem nos
                      permite atender desde pequenos estúdios até empresas
                      tradicionais com grandes fluxos de demanda.
                    </p>
                  </div>

                  <div>
                    <h2 className="font-sans text-base md:text-lg font-bold text-foreground mb-2 tracking-wide">
                      Tecnologia a Serviço da Segurança
                    </h2>
                    <p className="mb-2">
                      A inovação na FotoLivro Store tem um objetivo claro:
                      proteção e eficiência.
                    </p>
                    <ul className="list-disc list-inside space-y-1 pl-2 text-sm md:text-base">
                      <li>
                        Tecnologia própria para garantir a integridade de cada
                        projeto.
                      </li>
                      <li>
                        Infraestrutura robusta preparada para escala e grandes
                        volumes.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h2 className="font-sans text-base md:text-lg font-bold text-foreground mb-2 tracking-wide">
                      Curadoria e Design
                    </h2>
                    <p className="mb-2">
                      Onde o processo técnico encontra o olhar apurado:
                    </p>
                    <ul className="list-disc list-inside space-y-1 pl-2 text-sm md:text-base">
                      <li>
                        Equipe de designers experientes em diagramação de
                        álbuns.
                      </li>
                      <li>
                        Profissionais rigorosamente treinados para a curadoria,
                        garantindo uma seleção de imagens que conte a história
                        de forma intencional.
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h2 className="font-sans text-base md:text-lg font-bold text-foreground mb-2 tracking-wide">
                      Guardiões de Memórias
                    </h2>
                    <p>
                      Cada etapa é pensada para que o resultado final seja tão
                      especial quanto os momentos vividos. Nosso compromisso é
                      criar álbuns que vão durar gerações.
                    </p>
                  </div>

                  <p className="font-serif font-bold italic text-foreground text-base md:text-lg pt-2">
                    Confie suas memórias a quem se importa com elas.
                  </p>
                </div>

                <Button variant="elegant" size="lg" className="mt-8" asChild>
                  <a href="/" target="_blank" rel="noopener noreferrer">
                    Visite o site da FotoLivro Store
                  </a>
                </Button>
              </motion.div>

              {/* Vídeo à direita com frame de celular */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex justify-center"
              >
                {/* Phone Frame */}
                <div className="relative">
                  {/* Phone outer frame */}
                  <div className="relative bg-foreground rounded-[3rem] p-3 shadow-2xl">
                    {/* Phone notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-foreground rounded-b-2xl z-10" />

                    {/* Phone screen */}
                    <div className="relative bg-foreground rounded-[2.5rem] overflow-hidden">
                      {/* Video container */}
                      <div className="w-[280px] h-[500px] md:w-[320px] md:h-[570px]">
                        <iframe
                          src="https://www.youtube.com/embed/vtuJNwuUrCg"
                          title="FotoLivro Store"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="w-full h-full"
                        />
                      </div>
                    </div>

                    {/* Phone home indicator */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-muted rounded-full" />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}

export default SobreNos
